import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, Link, CircularProgress, TextField, Button, MenuItem, Select,
  FormControl, InputLabel, Divider, Alert, Snackbar,
  Tooltip, Stack, ToggleButton, ToggleButtonGroup, IconButton, Collapse,
} from '@mui/material'
import type { SelectChangeEvent } from '@mui/material'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { getSprint, draftWorkItem, createWorkItem, getTeamHours } from '../../api/client'
import type { ApiResponse, SprintItem, WorkItemDraft, CreateWorkItemRequest, TeamMemberHours, MemberWorkItem } from '../../api/types'
import { DonutChart } from './DonutChart'
import type { DonutSlice } from './DonutChart'

const WORK_ITEM_TYPES = ['User Story', 'Task', 'Bug', 'Feature']
const FIBONACCI = [1, 2, 3, 5, 8, 13]

const STATE_COLOR: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  New:      'default',
  Active:   'info',
  Resolved: 'success',
  Closed:   'success',
  Blocked:  'error',
}

const TYPE_COLOR: Record<string, string> = {
  Feature:      '#8b5cf6',
  'User Story': '#3b82f6',
  Task:         '#10b981',
  Bug:          '#ef4444',
}

// ── Sprint board ──────────────────────────────────────────────────────────────

function SprintBoard() {
  const [typeFilter, setTypeFilter] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['sprint'],
    queryFn: () => getSprint(),
    refetchInterval: 60 * 60 * 1000,
  })

  const board = data?.data

  if (isLoading) return <CircularProgress size={24} />
  if (error || !board) return (
    <Alert severity="warning">
      {data?.error?.message ?? 'Could not load sprint — check DefaultProject / DefaultTeam config and ADO PAT permissions.'}
    </Alert>
  )

  const countByType = (type: string) => board.items.filter((i: SprintItem) => i.workItemType === type).length
  const visibleItems = typeFilter ? board.items.filter((i: SprintItem) => i.workItemType === typeFilter) : board.items

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5} flexWrap="wrap">
        <Typography variant="h6" fontWeight={600}>{board.sprint}</Typography>
        <Chip label={board.project} size="small" variant="outlined" />
        <Chip label={`${board.items.length} items`} size="small" color="primary" variant="outlined" />
      </Stack>

      <ToggleButtonGroup
        value={typeFilter}
        exclusive
        onChange={(_: unknown, val: string | null) => setTypeFilter(val)}
        size="small"
        sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}
      >
        {['Feature', 'User Story', 'Task', 'Bug'].map((type: string) => (
          <ToggleButton key={type} value={type}
            sx={{ borderRadius: '16px !important', border: '1px solid !important',
              px: 1.5, fontSize: 12, fontWeight: 600, color: TYPE_COLOR[type],
              '&.Mui-selected': { bgcolor: `${TYPE_COLOR[type]}18`, borderColor: `${TYPE_COLOR[type]} !important` },
            }}>
            {type} <Box component="span" sx={{ ml: 0.75, fontWeight: 400, opacity: 0.7 }}>
              {countByType(type)}
            </Box>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'grey.50' } }}>
              <TableCell width={40}>#</TableCell>
              <TableCell>Title</TableCell>
              {!typeFilter && <TableCell width={110}>Type</TableCell>}
              <TableCell width={110}>State</TableCell>
              <TableCell width={150}>Assigned To</TableCell>
              <TableCell width={60} align="center">Pts</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  No {typeFilter ?? ''} items.
                </TableCell>
              </TableRow>
            ) : visibleItems.map((item: SprintItem) => (
              <TableRow key={item.id} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>
                  {item.id}
                </TableCell>
                <TableCell>
                  <Link href={item.url} target="_blank" rel="noopener"
                    sx={{ fontSize: 13, fontWeight: 500, color: 'text.primary', textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' } }}>
                    {item.title}
                  </Link>
                </TableCell>
                {!typeFilter && <TableCell>
                  <Typography fontSize={12} fontWeight={600}
                    sx={{ color: TYPE_COLOR[item.workItemType] ?? 'text.secondary' }}>
                    {item.workItemType}
                  </Typography>
                </TableCell>}
                <TableCell>
                  <Chip label={item.state} size="small"
                    color={STATE_COLOR[item.state] ?? 'default'} variant="outlined" />
                </TableCell>
                <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {item.assignedTo ?? '—'}
                </TableCell>
                <TableCell align="center" sx={{ fontSize: 12, fontWeight: 600 }}>
                  {item.storyPoints ?? '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  )
}

// ── Team hours chart ─────────────────────────────────────────────────────────

const MEMBER_COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b']

const TYPE_CHIP_COLOR: Record<string, string> = {
  Bug:          '#ef4444',
  Task:         '#10b981',
  'User Story': '#3b82f6',
  Feature:      '#8b5cf6',
}

function useMonthNav() {
  const now = new Date()
  const [year,  setYear]  = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1
  function prev() {
    if (month === 1) { setYear((y: number) => y - 1); setMonth(12) }
    else setMonth((m: number) => m - 1)
  }
  function next() {
    if (isCurrentMonth) return
    if (month === 12) { setYear((y: number) => y + 1); setMonth(1) }
    else setMonth((m: number) => m + 1)
  }
  const label = new Date(year, month - 1).toLocaleString('en-US', { month: 'long', year: 'numeric' })
  return { year, month, label, isCurrentMonth, prev, next }
}

function TeamHoursChart() {
  const nav = useMonthNav()
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['team-hours', nav.year, nav.month],
    queryFn: () => getTeamHours(undefined, undefined, nav.year, nav.month),
    refetchInterval: 30 * 60 * 1000,
  })

  const report = data?.data
  const activeMembers = (report?.members ?? []).filter((m: TeamMemberHours) => m.completedHours > 0)
  const selectedMember = activeMembers.find((m: TeamMemberHours) => m.email === selectedEmail) ?? null

  const allSlices: DonutSlice[] = activeMembers.map((m: TeamMemberHours, i: number) => ({
    label: m.displayName.split(' ')[0],
    value: m.completedHours,
    color: MEMBER_COLORS[i % MEMBER_COLORS.length],
  }))

  const displaySlices = selectedMember
    ? [{ label: selectedMember.displayName.split(' ')[0], value: selectedMember.completedHours, color: MEMBER_COLORS[activeMembers.indexOf(selectedMember) % MEMBER_COLORS.length] }]
    : allSlices

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5} flexWrap="wrap" gap={1}>
        <Typography variant="h6" fontWeight={600}>Monthly Completed Hours</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5,
          border: '1px solid #e2e8f0', borderRadius: 2, px: 0.5, bgcolor: '#fff' }}>
          <IconButton size="small" onClick={nav.prev}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" fontWeight={600} sx={{ minWidth: 110, textAlign: 'center', userSelect: 'none' }}>
            {nav.label}
          </Typography>
          <IconButton size="small" onClick={nav.next} disabled={nav.isCurrentMonth}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {report ? `${report.team} — ${report.period}` : 'Work logged this month per team member'}
      </Typography>

      {isLoading && <CircularProgress size={24} />}
      {(error || (data && !report)) && (
        <Alert severity="warning">{data?.error?.message ?? 'Could not load team hours'}</Alert>
      )}

      {report && (activeMembers.length === 0
        ? <Alert severity="info">No completed hours logged this month yet.</Alert>
        : (
          <Stack spacing={2}>
            {/* Chart + clickable legend */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
              <DonutChart
                slices={displaySlices}
                centerLabel={selectedMember ? selectedMember.displayName.split(' ')[0] : 'hours'}
              />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, justifyContent: 'center' }}>
                {activeMembers.map((m: TeamMemberHours, i: number) => {
                  const color = MEMBER_COLORS[i % MEMBER_COLORS.length]
                  const selected = m.email === selectedEmail
                  return (
                    <Box
                      key={m.email}
                      onClick={() => setSelectedEmail(selected ? null : m.email)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 1.5, py: 0.75, borderRadius: 2, cursor: 'pointer',
                        bgcolor: selected ? `${color}18` : 'transparent',
                        border: selected ? `1.5px solid ${color}` : '1.5px solid transparent',
                        '&:hover': { bgcolor: `${color}12` },
                        transition: 'all 0.15s',
                      }}
                    >
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
                        {m.displayName.split(' ')[0]}
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>{m.completedHours}h</Typography>
                    </Box>
                  )
                })}
              </Box>
            </Box>

            {/* Work items for selected member */}
            {selectedMember && (
              <Paper variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {selectedMember.displayName} — {selectedMember.workItemCount} item{selectedMember.workItemCount !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                <Table size="small">
                  <TableBody>
                    {selectedMember.workItems.map((wi: MemberWorkItem) => (
                      <TableRow key={wi.id} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary', width: 50 }}>
                          {wi.id}
                        </TableCell>
                        <TableCell>
                          <Link href={wi.url} target="_blank" rel="noopener"
                            sx={{ fontSize: 13, color: 'text.primary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                            {wi.title}
                          </Link>
                        </TableCell>
                        <TableCell sx={{ width: 110 }}>
                          <Typography fontSize={12} fontWeight={600}
                            sx={{ color: TYPE_CHIP_COLOR[wi.workItemType] ?? 'text.secondary' }}>
                            {wi.workItemType}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ width: 60, fontWeight: 700, fontSize: 13 }}>
                          {wi.completedHours}h
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}
            {/* WIQL query display */}
            {report.wiqlQuery && <WiqlQueryPanel query={report.wiqlQuery} />}
          </Stack>
        )
      )}
    </Box>
  )
}

// ── WIQL query panel ──────────────────────────────────────────────────────────

function WiqlQueryPanel({ query }: { query: string }) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  function copy() {
    navigator.clipboard.writeText(query).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        onClick={() => setOpen((o: boolean) => !o)}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
          color: '#64748b', fontSize: 12, fontWeight: 500,
          '&:hover': { color: '#334155' },
          userSelect: 'none',
        }}
      >
        <Box sx={{ fontSize: 12 }}>{open ? '▲' : '▼'}</Box>
        View WIQL query
      </Box>

      <Collapse in={open}>
        <Box sx={{ mt: 1, position: 'relative' }}>
          <Box
            component="pre"
            sx={{
              m: 0, p: 2,
              bgcolor: '#0f172a', color: '#e2e8f0',
              fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6,
              borderRadius: 2, overflowX: 'auto',
              whiteSpace: 'pre-wrap', wordBreak: 'break-all',
            }}
          >
            {query}
          </Box>
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
              <Box
                onClick={copy}
                sx={{
                  cursor: 'pointer', fontSize: 11, fontWeight: 600,
                  color: copied ? '#10b981' : '#94a3b8',
                  bgcolor: '#1e293b', px: 1.5, py: 0.5, borderRadius: 1,
                  '&:hover': { color: '#e2e8f0' },
                  transition: 'color 0.15s',
                }}
              >
                {copied ? '✓ Copied' : 'Copy'}
              </Box>
            </Tooltip>
          </Box>
        </Box>
      </Collapse>
    </Box>
  )
}

// ── AI Story creator ──────────────────────────────────────────────────────────

function StoryCreator() {
  const qc = useQueryClient()
  const [description, setDescription] = useState('')
  const [preferredType, setPreferredType] = useState('')
  const [draft, setDraft] = useState<WorkItemDraft | null>(null)
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null)

  const draftMutation = useMutation({
    mutationFn: () => draftWorkItem(description, preferredType || undefined),
    onSuccess: (res: ApiResponse<WorkItemDraft>) => {
      console.log('[draft] response:', JSON.stringify(res))
      if (res.data) setDraft(res.data)
    },
  })

  const createMutation = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('no draft')
      const req: CreateWorkItemRequest = {
        workItemType: draft.workItemType,
        title: draft.title,
        description: draft.description,
        acceptanceCriteria: draft.acceptanceCriteria,
        tags: draft.tags,
        storyPoints: draft.storyPoints,
      }
      return createWorkItem(req)
    },
    onSuccess: (res: ApiResponse<SprintItem>) => {
      if (res.data) {
        setToast({ msg: `Created #${res.data.id}: ${res.data.title}`, severity: 'success' })
        setDraft(null)
        setDescription('')
        qc.invalidateQueries({ queryKey: ['sprint'] })
      }
    },
    onError: () => setToast({ msg: 'Failed to create work item', severity: 'error' }),
  })

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} mb={0.5}>AI Story Creator</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Describe the work in plain English. Azure OpenAI will structure it into a proper work item.
      </Typography>

      <Stack spacing={2}>
        <TextField
          label="Describe the work"
          multiline minRows={3}
          placeholder="e.g. We need to set up monitoring alerts for the AKS cluster so we get paged when pods are crash-looping"
          value={description}
          onChange={(e: { target: { value: string } }) => setDescription(e.target.value)}
          fullWidth
        />

        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Preferred type</InputLabel>
            <Select value={preferredType} label="Preferred type"
              onChange={(e: SelectChangeEvent<string>) => setPreferredType(e.target.value)}>
              <MenuItem value=""><em>Let AI decide</em></MenuItem>
              {WORK_ITEM_TYPES.map((t: string) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>

          <Button
            variant="contained" startIcon={<AutoFixHighIcon />}
            disabled={!description.trim() || draftMutation.isPending}
            onClick={() => draftMutation.mutate()}
          >
            {draftMutation.isPending ? 'Drafting…' : 'Draft with AI'}
          </Button>
        </Stack>

        {draftMutation.isError && (
          <Alert severity="error">AI call failed — check your Azure OpenAI config.</Alert>
        )}
      </Stack>

      {draft && (
        <Paper variant="outlined" sx={{ mt: 3, p: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Typography variant="subtitle1" fontWeight={700}>Draft Preview</Typography>
            <Stack direction="row" spacing={1}>
              <Chip label={draft.workItemType} size="small"
                sx={{ bgcolor: TYPE_COLOR[draft.workItemType] ?? undefined, color: 'white', fontWeight: 600 }} />
              <Chip label={`${draft.storyPoints} pts`} size="small" variant="outlined" />
              {draft.tags.map((t: string) => <Chip key={t} label={t} size="small" variant="outlined" />)}
            </Stack>
          </Stack>

          <TextField label="Title" value={draft.title} fullWidth size="small" sx={{ mb: 2 }}
            onChange={(e: { target: { value: string } }) => setDraft({ ...draft, title: e.target.value })} />

          <TextField label="Description (User Story)" value={draft.description} fullWidth
            multiline minRows={2} size="small" sx={{ mb: 2 }}
            onChange={(e: { target: { value: string } }) => setDraft({ ...draft, description: e.target.value })} />

          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
            ACCEPTANCE CRITERIA
          </Typography>
          <Stack spacing={1} mb={2}>
            {draft.acceptanceCriteria.map((ac: string, i: number) => (
              <TextField key={i} value={ac} size="small" fullWidth
                onChange={(e: { target: { value: string } }) => {
                  const updated = [...draft.acceptanceCriteria]
                  updated[i] = e.target.value
                  setDraft({ ...draft, acceptanceCriteria: updated })
                }} />
            ))}
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Story Points</InputLabel>
              <Select value={draft.storyPoints} label="Story Points"
                onChange={(e: SelectChangeEvent<number>) => setDraft({ ...draft, storyPoints: Number(e.target.value) })}>
                {FIBONACCI.map((n: number) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </FormControl>

            <Box flex={1} />

            <Tooltip title="Edit directly in ADO after creation">
              <Button
                variant="contained" color="success" startIcon={<AddCircleOutlineIcon />}
                disabled={createMutation.isPending}
                onClick={() => createMutation.mutate()}
              >
                {createMutation.isPending ? 'Creating…' : 'Create in ADO'}
              </Button>
            </Tooltip>
          </Stack>
        </Paper>
      )}

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast?.severity ?? 'success'} onClose={() => setToast(null)}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ScrumPage() {
  return (
    <Stack spacing={4}>
      <SprintBoard />
      <Divider />
      <TeamHoursChart />
      <Divider />
      <StoryCreator />
    </Stack>
  )
}
